import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { setParentalPin, verifyParentalPin } from '../../network/service';
import Translate from '../../Components/Multilanguage/Translate';

const NUMPAD_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'del'];

const ParentalPasscodeModal = ({
  isOpen = false,
  mode = 'verify',
  appInfo = {},
  showClose = false,
  onClose = () => {},
  onSuccess = () => {},
}) => {
  const [digits, setDigits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setDigits([]);
      setError('');
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        handleKey(Number(e.key));
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        handleKey('del');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, digits, loading]);

  if (!isOpen) return null;

  const handleKey = (key) => {
    if (loading) return;
    if (key === 'del') {
      setDigits((prev) => prev.slice(0, -1));
      setError('');
      return;
    }
    if (digits.length >= 4) return;
    setError('');
    const next = [...digits, String(key)];
    setDigits(next);
    if (next.length === 4) {
      handleComplete(next);
    }
  };

  const handleClose = () => {
    if (loading) return;
    try { onClose(); } catch (e) {}
  };

  const handleComplete = async (code) => {
    const pin = code.join('');
    setLoading(true);
    if (mode === 'setup') {
      try {
        const res = await setParentalPin(appInfo, pin);
        if (res?.status === 200) {
          try { onSuccess(); } catch (e) {}
        } else {
          const message = res?.data?.message;
          setError(res?.data?.message);
          setDigits([]);
        }
      } catch (err) {
        const message = err?.response?.data?.message;
        setError(message);
        setDigits([]);
      }
    } else {
      try {
        const res = await verifyParentalPin(appInfo, pin);
        if (res?.status === 200) {
          try { onSuccess(); } catch (e) {}
        } else {
          const message = res?.data?.message;
          setError(message);
          setDigits([]);
        }
      } catch (err) {
        const message = err?.response?.data?.message;
        setError(message);
        setDigits([]);
      }
    }
    setLoading(false);
  };

  return (
    <div
      className={`parental-passcode-overlay parental-passcode-overlay--${mode}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={`parental-passcode-panel parental-passcode-panel--${mode}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'setup' ? 'Set parental passcode' : 'Enter parental passcode'}
      >
        {showClose && (
          <button aria-label="Close" className="parental-passcode__close" onClick={handleClose}>
            ×
          </button>
        )}
        <p className="parental-passcode__label"><Translate textKey={'parents_only'} /></p>
        <h2 className="parental-passcode__title">
          {mode === 'setup' ? (
            <Translate textKey={'set_four_digit_passcode'} />
          ) : (
            <Translate textKey={'enter_four_digit_passcode'} />
          )}
        </h2>

        <div className="parental-passcode__slots" aria-label="Passcode input">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`parental-passcode__slot${digits[i] !== undefined ? ' parental-passcode__slot--filled' : ''}`}
              aria-hidden="true"
            />
          ))}
        </div>

        {error && (
          <p className="parental-passcode__error" role="alert">{error}</p>
        )}

        <div className="parental-passcode__numpad" role="group" aria-label="Number pad">
          {NUMPAD_KEYS.map((key, idx) => {
            if (key === null) {
              return <div key={idx} className="parental-passcode__key-empty" aria-hidden="true" />;
            }
            if (key === 'del') {
              return (
                <button
                  key={idx}
                  className="parental-passcode__key parental-passcode__key--del"
                  onClick={() => handleKey('del')}
                  disabled={loading || digits.length === 0}
                  aria-label="Delete last digit"
                >
                  ‹
                </button>
              );
            }
            return (
              <button
                key={idx}
                className="parental-passcode__key"
                onClick={() => handleKey(key)}
                disabled={loading || digits.length >= 4}
                aria-label={String(key)}
              >
                {key}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ParentalPasscodeModal;
